import { Check } from "lucide-react";

const transitionDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

export const PricingCard = ({ tier: t, loading, onCheckout, onQuote }) => (
  <article
    data-testid={`pricing-card-${t.id}`}
    className={`relative flex h-full flex-col rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
      t.popular ? "border-gold/50 bg-navy text-white shadow-2xl shadow-navy/40" : "border-navy/10 bg-cream hover:border-gold/40 hover:shadow-xl hover:shadow-gold/10"
    }`}
  >
    {t.popular && (
      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-navy">
        {t.badge}
      </span>
    )}
    <div className="flex items-baseline justify-between gap-3">
      <h3 className="font-display text-2xl font-bold">{t.name}</h3>
      {!t.popular && <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-gold-dark">{t.badge}</span>}
    </div>
    <p className={`mt-1 text-sm ${t.popular ? "text-white/70" : "text-slate-500"}`}>{t.tagline}</p>
    <p className="mt-6 font-display text-5xl font-bold tracking-tight">
      {t.price.replace(" €", "")}
      <span className={`ml-1 text-2xl ${t.popular ? "text-gold" : "text-gold-dark"}`}>€</span>
    </p>
    <ul className={`mt-7 flex-1 space-y-3 border-t pt-7 text-sm ${t.popular ? "border-white/10" : "border-navy/10"}`}>
      {t.features.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${t.popular ? "bg-gold/20 text-gold" : "bg-gold/15 text-gold-dark"}`}>
            <Check size={12} strokeWidth={3} />
          </span>
          <span className={t.popular ? "text-white/85" : "text-slate-700"}>{f}</span>
        </li>
      ))}
    </ul>
    <button
      data-testid={`pricing-cta-${t.id}`}
      onClick={onCheckout}
      disabled={loading}
      className={`mt-8 rounded-full px-6 py-3.5 text-sm font-semibold transition-all disabled:opacity-60 ${
        t.popular ? "bg-gold text-navy hover:bg-gold-light hover:shadow-lg hover:shadow-gold/30" : "bg-navy text-white hover:bg-navy-light hover:shadow-lg hover:shadow-gold/20"
      }`}
    >
      {loading ? "Redirection paiement…" : t.cta}
    </button>
    <button
      data-testid={`pricing-devis-${t.id}`}
      onClick={onQuote}
      className={`mt-3 text-xs font-semibold underline-offset-4 transition-colors hover:underline ${t.popular ? "text-white/60 hover:text-gold" : "text-slate-500 hover:text-navy"}`}
    >
      Ou demander un devis d'abord
    </button>
  </article>
);

export const MaintenanceOffer = ({ loading, onCheckout }) => (
  <div
    data-testid="pricing-maintenance-card"
    className="relative mx-auto mt-10 max-w-3xl overflow-hidden rounded-3xl border border-gold/40 bg-navy p-6 text-white shadow-2xl shadow-navy/30 sm:p-8"
  >
    <span className="absolute right-5 top-5 rounded-full bg-gold px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy">
      Abonnement unique
    </span>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-light">Maintenance + Hébergement</p>
    <h3 className="mt-3 font-display text-xl font-bold sm:text-2xl">
      Votre site en ligne, à jour, sans y penser.
    </h3>
    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="rounded-2xl border border-gold/40 bg-white/5 p-4" data-testid="pricing-maintenance-intro-price">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold-light">Phase 1 — 3 premiers mois</p>
        <p className="mt-2 font-display text-4xl font-bold tracking-tight">
          14,99<span className="ml-1 text-xl text-gold">€</span><span className="ml-1 text-base font-semibold text-white/70">/mois</span>
        </p>
        <p className="mt-1 text-xs text-white/70">3 mensualités, prélevées chaque mois</p>
      </div>
      <div className="rounded-2xl border border-white/15 bg-white/5 p-4" data-testid="pricing-maintenance-yearly-price">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/60">Phase 2 — ensuite</p>
        <p className="mt-2 font-display text-4xl font-bold tracking-tight">
          35,99<span className="ml-1 text-xl text-gold">€</span><span className="ml-1 text-base font-semibold text-white/70">/an</span>
        </p>
        <p className="mt-1 text-xs text-white/70">soit 2,99 €/mois, facturé une fois par an</p>
      </div>
    </div>
    <p className="mt-4 rounded-xl bg-gold/15 px-4 py-2.5 text-xs leading-relaxed text-white/90" data-testid="pricing-maintenance-transition">
      Bascule automatique vers le tarif annuel le <strong className="text-gold">{transitionDate()}</strong> si vous souscrivez aujourd'hui — même abonnement, même carte, aucune double facturation.
    </p>
    <ul className="mt-6 grid grid-cols-1 gap-2 text-sm text-white/85 sm:grid-cols-2">
      <li>• Hébergement sécurisé (https) chez OVHcloud, sauvegardes incluses</li>
      <li>• Modifications à la demande (textes, photos, horaires…)</li>
      <li>• Mises à jour techniques incluses</li>
      <li>• Sans engagement, résiliable à tout moment</li>
    </ul>
    <button
      data-testid="pricing-cta-maintenance"
      onClick={onCheckout}
      disabled={loading}
      className="mt-7 w-full rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-navy transition-all hover:bg-gold-light hover:shadow-lg hover:shadow-gold/30 disabled:opacity-60 sm:w-auto"
    >
      {loading ? "Redirection paiement…" : "S'abonner — 14,99 €/mois"}
    </button>
  </div>
);
